import React from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Grid,
  GridItem,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { DownloadIcon } from "@chakra-ui/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FiAward } from "react-icons/fi";
import { CrudPage } from "../components/CrudPage";
import { FundCard } from "../components/funds/FundCard";
import { certificates } from "../services/api";
import { useFunds } from "../hooks/useFunds";
import { useAuth } from "../context/AuthContext";
import {
  getUserIdentifier,
  getUserIdentifierLabel,
} from "../utils/userIdentity";

const CERTIFICATE_FUND_CODES = new Set(["11141", "11343", "11383"]);

const triggerBlobDownload = (
  blob: Blob,
  fallbackFileName: string,
  dispositionHeader?: string,
) => {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  const filenameMatch = String(dispositionHeader || "").match(
    /filename="?([^"]+)"?/,
  );

  link.href = blobUrl;
  link.download = filenameMatch?.[1]
    ? decodeURIComponent(filenameMatch[1])
    : fallbackFileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

const getBackendErrorMessage = async (error: any) => {
  const payload = error?.response?.data;

  if (payload instanceof Blob) {
    try {
      const text = await payload.text();
      const parsed = JSON.parse(text);
      return parsed?.message || error?.message || "خطای ناشناخته";
    } catch (_parseError) {
      return error?.message || "خطای ناشناخته";
    }
  }

  return payload?.message || error?.message || "خطای ناشناخته";
};

const columns = [
  {
    header: "شناسه رهگیری",
    accessor: "certificateTrackId",
  },
  {
    header: "صندوق",
    accessor: "fundName",
    render: (item: any) => item.fundName || item.fundTitle || "—",
  },
  {
    header: "سرمایه‌گذار",
    accessor: "investor",
    render: (item: any) =>
      item.investor?.fullName ||
      item.nationalCode ||
      item.investor?.nationalId ||
      "—",
  },
  {
    header: "تاریخ ایجاد",
    accessor: "createdAt",
    render: (item: any) =>
      item.createdAt
        ? new Date(item.createdAt).toLocaleDateString("fa-IR")
        : "—",
  },
  {
    header: "تعداد واحد",
    accessor: "count",
    render: (item: any) =>
      item.count !== undefined && item.count !== null && item.count !== ""
        ? Number(item.count).toLocaleString("fa-IR")
        : "—",
  },
  {
    header: "دانلود",
    accessor: "download",
    render: (item: any) => <DownloadCertificateButton item={item} />,
    excludeFromExport: true,
  },
];

const DownloadCertificateButton = ({ item }: { item: any }) => {
  const toast = useToast();

  const handleDownload = async () => {
    try {
      const response = await certificates.download(item._id);
      triggerBlobDownload(
        response.data,
        `certificate-${item.certificateTrackId || item._id}.pdf`,
        response.headers["content-disposition"],
      );
    } catch (error: any) {
      const message = await getBackendErrorMessage(error);
      toast({
        title: "دریافت فایل گواهی ناموفق بود.",
        description: message,
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    }
  };

  return (
    <Button
      size="sm"
      variant="outline"
      leftIcon={<DownloadIcon />}
      onClick={() => void handleDownload()}
    >
      PDF
    </Button>
  );
};

export const CertificatesPage: React.FC = () => {
  const { user } = useAuth();
  const identifier = getUserIdentifier(user);
  const identifierLabel = getUserIdentifierLabel(user);
  const [loadingFundKey, setLoadingFundKey] = React.useState<string | null>(
    null,
  );
  const { data: fundsData, isLoading: isFundsLoading } = useFunds();
  const queryClient = useQueryClient();
  const toast = useToast();
  const mutedColor = useColorModeValue("gray.500", "gray.400");

  const createMutation = useMutation(certificates.create, {
    onSuccess: (response) => {
      queryClient.invalidateQueries(["certificates"]);
      triggerBlobDownload(
        response.data,
        `certificate-${loadingFundKey || "download"}.pdf`,
        response.headers["content-disposition"],
      );
      toast({
        title: "گواهی سرمایه‌گذاری ایجاد شد.",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    },
    onError: async (error: any) => {
      const message = await getBackendErrorMessage(error);
      toast({
        title: "ایجاد گواهی سرمایه‌گذاری ناموفق بود.",
        description: message,
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    },
    onSettled: () => {
      setLoadingFundKey(null);
    },
  });

  if (!identifier) {
    return (
      <Alert status="warning" borderRadius="lg">
        <AlertIcon />
        {identifierLabel} کاربر برای دریافت گواهی سرمایه‌گذاری یافت نشد.
      </Alert>
    );
  }

  const funds = (fundsData?.items || []).filter((fund: any) =>
    CERTIFICATE_FUND_CODES.has(String(fund.fundRegisterCode || "").trim()),
  );

  return (
    <VStack w="100%" h="100%" spacing={0} align="stretch">
      <Box flex={1} overflowY="auto" overflowX="hidden" pr={{ base: 0, md: 1 }}>
        {isFundsLoading ? (
          <VStack justify="center" h="100%" py={10} spacing={3}>
            <Spinner size="lg" />
            <Text color={mutedColor}>در حال دریافت صندوق‌ها...</Text>
          </VStack>
        ) : (
          <Grid templateColumns="repeat(auto-fit, minmax(240px, 1fr))" gap={5}>
            {funds.map((fund: any) => {
              return (
                <GridItem key={fund.fundRegisterCode}>
                  <FundCard
                    variant="short"
                    fund={fund}
                    showSecondaryAction={false}
                    primaryAction={{
                      label: "صدور گواهی",
                      icon: FiAward,
                      onClick: () => {
                        const fundKey = String(
                          fund.nav?.seoRegisterNo || fund.fundRegisterCode,
                        );
                        setLoadingFundKey(fundKey);
                        createMutation.mutate({
                          fundSeoRegisterNo: String(
                            fund.nav?.seoRegisterNo || fund.fundRegisterCode,
                          ),
                          fundName: fund.fundName,
                        });
                      },
                      isLoading:
                        createMutation.isLoading &&
                        loadingFundKey ===
                          String(
                            fund.nav?.seoRegisterNo || fund.fundRegisterCode,
                          ),
                    }}
                  />
                </GridItem>
              );
            })}
          </Grid>
        )}
      </Box>
    </VStack>
  );
};
